import _ from 'lodash';
/**
 * Data transformer service for personal loan calc.
 * Transforms data input to format acceptable to application.
 * @module dataTransformer
 */

/**
* Transforms data to format acceptable by ui module
* @param {array} rawData - data to be transformed
* @return {object} - transformed data
* @example
* // data structure like this
* [
    {
      category: 'walk_in',
      insurance: true,
      sum_min: 90000,
      sum_max: 2000000,
      term_min: 13,
      term_max: 60,
      rate_start: 0,
      rate_finish: 12,
      rate: 11.99
    },
    {
      category: 'walk_in',
      insurance: true,
      sum_min: 90000,
      sum_max: 2000000,
      term_min: 13,
      term_max: 60,
      rate_start: 13,
      rate_finish: 60,
      rate: 9.99
    },
    {
      category: 'walk_in',
      insurance: false,
      sum_min: 90000,
      sum_max: 2000000,
      term_min: 13,
      term_max: 60,
      rate_start: 0,
      rate_finish: 60,
      rate: 15.99
    }
 ]
* // will be transformed to
* {
*   'walk_in': {
*     'amount': {
*       'min': 90000
*       'max': 2000000
*     },
*     'term': {
*       'min': 13,
*       'max': 60,
*     }
*   }
* }
*/

const transformData = ({ rawData, rawDefaultValues }) => {
    const rawDataCloned = _.map(_.cloneDeep(rawData), (item) => transformKeys(item));
    const rawDefaultValuesClone = transformKeys(rawDefaultValues);
    const categoryList = _.uniq(_.map(rawData, 'category'));

    let transformed = {
        data: {},
        defaultValues: rawDefaultValuesClone,
    };

    _.each(categoryList, (name) => {
        const itemsForCategoryName = _.filter(rawDataCloned, (item) => item.category === name);
        const amounts = _.union(_.map(itemsForCategoryName, 'amountMin'), _.map(itemsForCategoryName, 'amountMax'));
        let terms;
        let termMin;
        let termMax;

        terms = _.sortBy(
            _.uniqBy(
                _.map(itemsForCategoryName, (item) => ({ min: item.termMin, max: item.termMax })),
                'min'
            ),
            'min'
        );
        termMin = Math.min(..._.map(terms, 'min'));
        termMax = Math.max(..._.map(terms, 'max'));

        transformed.data[name] = {
            amount: {
                min: Math.min(...amounts),
                max: Math.max(...amounts),
            },
            term: {
                min: termMin,
                max: termMax,
                values: terms,
            },
        };
    });

    return transformed;
};

/**
 * Transforms data keys to format acceptable by ui module
 * @param {object} rawData - data to be transformed
 * @return {object} rawData - transformed data
 */
const transformKeys = (rawData) => {
    const rawPropsToTransformedMap = {
        term_min: 'termMin',
        term_max: 'termMax',
        sum_min: 'amountMin',
        sum_max: 'amountMax',
    };
    const rawDataClone = _.cloneDeep(rawData);

    _.each(rawDataClone, (val, key) => {
        const rawPropsToTransformedMapKey = rawPropsToTransformedMap[key];

        if (rawPropsToTransformedMapKey) {
            rawDataClone[rawPropsToTransformedMapKey] = val;
            rawDataClone[key] = null;
            delete rawDataClone[key];
        }
    });

    return rawDataClone;
};

export { transformData, transformKeys };
